/**
 * The base view class in Foundation.js. It takes a selector string or
 * a jQuery object of elements as its lone constructor argument. And here's
 * the ideal we're trying to acheive: the only place you should see jQuery API
 * within Foundation.js is inside of Foudnation.View and its subclasses.
 * @constructor
 * @extends {Foundation.Observable}
 * @param {jQuerySelector} jqObjOrSelector
 */
Foundation.View = function (jqObjOrSelector) {
    Foundation.Observable.call(this);

    /**
     * The jQuery object containing the root-level element for this view.
     * This element is "protected", so that only subclasses of Foundation.View
     * are allowed to access it.
     * @protected
     * @type {jQuery}
     */
    this.element = jQuery(jqObjOrSelector).first();

    /**
     * @private
     * @type {boolean|undefined}
     */
    this._viewIsHidden = undefined;

    /**
     * @private
     * @type {Foundation.Observable}
     */
    this._dataSource = null;

    /**
     * @private
     * @type {Foundation.Promise}
     */
    this._needsDOMUpdatePromise = null;

    // Record that a Foundation.View has been instantiated for this element.
    this.element.data("viewInstance", true);

    // Automatically instantiate named subviews.
    this.instantiateSubviews();
}
Foundation.inherit(Foundation.View, Foundation.Observable);

/**
 * "Draw" the view. This method is called whenever a view's datasources
 * change. This method is meant to be overriden by subclasses, so it's
 * just a stub that throws an error. Even when implemented by a subclass,
 * this method should never be called directly by any class other than
 * Foundation.View. Even subclasses of Foundation.View should not call
 * their own implementations of it.
 * @protected
 */
Foundation.View.prototype.updateDOM = function () {
    throw new Foundation.View.Error("draw() method called but not implemented");
};

/**
 * Register a Foundation.Observable instance as the data source for this
 * view. Only subclasses of Foundation.View should call this method.
 * @protected
 * @param {Foundation.Observable} theDataSource
 */
Foundation.View.prototype.setDataSource = function (theDataSource) {
    // If we already have a data source set, we want to stop getting
    // notifications of changes in the old data source.
    if (this._dataSource) {
        this._dataSource.unbind("changed", this);
    }

    this._dataSource = theDataSource;

    // Draw the view whenever the data source changes.
    this._dataSource.bind("changed", this, this.setNeedsDOMUpdate);

    // We've changed data sources, so set's make sure we get redrawn.
    this.setNeedsDOMUpdate();
};

/**
 * Notify the view that the data source backing it has changed, so it
 * needs to redisplay.
 */
Foundation.View.prototype.setNeedsDOMUpdate = function () {
    if (!this._needsDOMUpdatePromise) {
        this._needsDOMUpdatePromise = new Foundation.Promise()
            .always(function () { this._needsDisplayTimer = null }.bind(this))
            .then(this.updateDOM.bind(this))
            .marry(Foundation.View._getGlobalNeedsDisplayTimer());
    }
};

/**
 * @private
 * @type {Foundation.Timer}
 */
Foundation.View._globalNeedsDisplayTimer = null;
Foundation.View._getGlobalNeedsDisplayTimer = function () {
    if (!Foundation.View._globalNeedsDisplayTimer) {
        Foundation.View._globalNeedsDisplayTimer = new Foundation.Timer(1);
        Foundation.View._globalNeedsDisplayTimer.always(function () {
            Foundation.View._globalNeedsDisplayTimer = null;
        });
    }
    return Foundation.View._globalNeedsDisplayTimer;
};

/**
 * Automatically instantiates Foundation.View objects for elements marked with
 * data-view="name". The instance is stashed in this view's observable
 * datastore, keyed by the name supplied. A subclass of Foundation.View
 * can be supplied with a data-view-subclass="Foundation.View.Class" attribute.
 * @public
 */
Foundation.View.prototype.instantiateSubviews = function () {
    var subviewElements = this.element.find("[data-view]");
    for (var i = 0; i < subviewElements.length; i++) {
        var subviewElement = jQuery(subviewElements[i]);

        if (!subviewElement.data("viewInstantiated")) {
            var viewSubclassStr = `${subviewElement.data("viewSubclass") || ""}`;
            var subview = Foundation.View._instanceFromElementAndSubclassPath(
                subviewElement,
                viewSubclassStr
            );
            var subviewName = `${subviewElement.data("view")}`;
            this.set(subviewName, subview);
            subviewElement.data("viewInstantiated", true);
        }
    }
};

/**
 * Is this view hidden? Note this does not check parent views,
 * which may in fact be hidden.
 * @return {boolean}
 */
Foundation.View.prototype.isHidden = function () {
    if (this._viewIsHidden === undefined) {
        this._viewIsHidden = (this.element.css("display") === "none");
    }
    return this._viewIsHidden;
};

/**
 * Get a Foundation.View instance based on an element and an optional
 * dot-path string to a Foundation.View subclass constructor.
 * @private
 * @param {jQuery} element
 * @param {string=} subclassPathStr
 * @return {Foundation.View}
 */
Foundation.View._instanceFromElementAndSubclassPath = function (element, subclassPathStr) {
    var viewSubclass = Foundation.View;

    if (subclassPathStr) {
        // Lots of room for things to go wrong here. The path may
        // be malformed. The path might be misspelled. The path
        // might not point to a subclass of Foundation.View. So
        // we catch all those errors and emit a warning.
        try {
            viewSubclass = Foundation.View._viewSubclassSpecifiedByPath(subclassPathStr);
        }
        catch (e) {
            Foundation.warn(e, element[0]);
        }
    }

    return new viewSubclass(element);
};

/**
 * Find the specified subclass of Foundation.View.
 * @private
 * @param {string} pathStr
 * @return {Function}
 */
Foundation.View._viewSubclassSpecifiedByPath = function (pathStr) {
    var path = new Foundation.Path(pathStr);
    var possibleSubclasses = path.findSpecifiedObjectsIn(window);
    if (!possibleSubclasses) {
        throw new Foundation.View.Error(`${pathStr}: Couldn't find view subclass`)
    }

    // There should never be more than one match, so let's just
    // log a warning if there is.
    if (possibleSubclasses.length > 1) {
        Foundation.warn(`${pathStr}: Found more than one match`);
    }

    // Ensure we've got a subclass constructor.
    var possibleSubclass = possibleSubclasses[0];
    if (!Foundation.verifyObjectIsConstructorAndInheritsFrom(possibleSubclass, Foundation.View)) {
        throw new Foundation.View.Error(`${pathStr}: Not a subclass of Foundation.View`);
    }

    // Now we know we've got a valid Foundation.View constructor.
    return possibleSubclass;
};

/**
 * @constructor
 * @extends {Error}
 * @param {string} message
 */
Foundation.View.Error = function (message) {
    Error.call(this, message);
    this.name = "ViewError";
};
Foundation.inherit(Foundation.View.Error, Error);

/**
 * Utility method to find the current highest z-index in the given element.
 * It will not include elements that are not currently visible.
 * @param {jQuerySelector} jqObjOrSelector
 * @return {number}
 */
Foundation.View.highestZIndexFor = function (jqObjOrSelector) {
  var root = jQuery(jqObjOrSelector).first()
  return root
    .find(">*:visible")
    .not((_, elem) => (
      elem.style.position === 'absolute' &&
      elem.style.visibility === 'hidden'
    ))
    .toArray()
    .reduce((prev, cur) => {
      cur = jQuery(cur).css("zIndex") || "0"
      cur = parseInt(cur, 10)
      return cur > prev ? cur : prev
    }, 0)
}

window["Foundation"]["View"] = Foundation.View;

/**
 * @namespace Desktop widget implementations.
 */
Foundation.View.Desktop = {};
window["Foundation"]["View"]["Desktop"] = {};

/**
 * @namespace Mobile widget implentations.
 */
Foundation.View.Mobile = {};
window["Foundation"]["View"]["Mobile"] = {};

/**
 * @namespace Responsive widget implentations.
 */
Foundation.View.Mobile = {};
window["Foundation"]["View"]["Responsive"] = {};
